/**
 * NewBranchDialog — modal for creating a new CoW branch.
 * Supports nested branching: can fork from any existing branch (not just main).
 */

import { useState } from "react";
import { Box, Button, Dialog, Flex, Select, Text, TextField } from "@radix-ui/themes";

import { useProvenanceContext } from "../../context/ProvenanceContext";
import { useBranches, useCreateBranch } from "../../hooks/useVersioning";

const BRANCH_NAME_RE = /^[a-zA-Z0-9][a-zA-Z0-9./_-]*$/;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NewBranchDialog({ open, onOpenChange }: Props) {
  const { projectId, currentBranch, setBranch } = useProvenanceContext();
  const { data: existingBranches } = useBranches(projectId);
  const createMutation = useCreateBranch(projectId);
  const [name, setName] = useState("");
  const [parentBranch, setParentBranch] = useState(currentBranch);
  const [error, setError] = useState<string | null>(null);

  function handleCreate() {
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Branch name is required");
      return;
    }
    if (!BRANCH_NAME_RE.test(trimmed)) {
      setError("Use letters, numbers, dashes, dots, underscores, or slashes");
      return;
    }
    if (trimmed === "main") {
      setError("Cannot create a branch named 'main'");
      return;
    }

    createMutation.mutate({ name: trimmed, parentBranch }, {
      onSuccess: () => {
        setBranch(trimmed);
        setName("");
        setError(null);
        setParentBranch("main");
        onOpenChange(false);
      },
      onError: (err) => {
        setError(err instanceof Error ? err.message : String(err));
      },
    });
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content maxWidth="420px">
        <Dialog.Title>Create Branch</Dialog.Title>
        <Dialog.Description size="2" color="gray">
          Fork a new branch from any existing branch.
        </Dialog.Description>

        <Flex direction="column" gap="3" mt="4">
          <Box>
            <Text as="label" size="2" weight="medium" mb="1" style={{ display: "block" }}>
              Parent branch
            </Text>
            <Select.Root value={parentBranch} onValueChange={setParentBranch}>
              <Select.Trigger style={{ width: "100%" }} />
              <Select.Content>
                {existingBranches?.map((b) => (
                  <Select.Item key={b.id} value={b.name}>
                    {b.name}{b.name === currentBranch ? " (current)" : ""}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </Box>

          <Box>
            <Text as="label" size="2" weight="medium" mb="1" style={{ display: "block" }}>
              Branch name
            </Text>
            <TextField.Root
              placeholder="feature/my-experiment"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError(null);
              }}
              onKeyDown={(e) => e.key === "Enter" && handleCreate()}
            />
          </Box>

          {error && (
            <Text size="1" color="red">{error}</Text>
          )}
        </Flex>

        <Flex gap="3" mt="4" justify="end">
          <Dialog.Close>
            <Button variant="soft" color="gray">Cancel</Button>
          </Dialog.Close>
          <Button
            onClick={handleCreate}
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? "Creating..." : "Create Branch"}
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}